<ng-container *ngIf="open">

  <!-- Backdrop -->
  <div class="vd-backdrop" (click)="closeDrawer.emit()" aria-hidden="true"></div>

  <!-- Drawer -->
  <div class="vd-panel" role="dialog" aria-modal="true" [attr.aria-label]="'settings.splitting_rules_drawer_title_new' | translate">

    <!-- ── Header ─────────────────────────────────────────────────────── -->
    <div class="vd-header">
      <span class="vd-header__title">
        {{ rule ? ('settings.splitting_rules_drawer_title_edit' | translate) : ('settings.splitting_rules_drawer_title_new' | translate) }}
      </span>
      <button class="vd-close-btn" type="button" (click)="closeDrawer.emit()" [attr.aria-label]="'settings.splitting_rules_drawer_close' | translate">
        <ui-svg-icon svgIcon="close" size="medium"></ui-svg-icon>
      </button>
    </div>

    <!-- ── Body ───────────────────────────────────────────────────────── -->
    <div class="vd-body">

      <!-- Rule name -->
      <div class="vd-section" [formGroup]="form">
        <label class="vd-label" for="rule-name">{{ 'settings.splitting_rules_rule_name' | translate }}</label>
        <input
          id="rule-name"
          class="vd-input"
          [class.vd-input--error]="nameAlreadyExists"
          type="text"
          formControlName="name"
          [placeholder]="'settings.splitting_rules_rule_name_placeholder' | translate"
          autocomplete="off" />
        <span class="vd-error-hint" *ngIf="nameAlreadyExists">
          {{ 'settings.splitting_rules_name_exists' | translate }}
        </span>
      </div>

      <div class="vd-divider"></div>

      <!-- ── When to split ────────────────────────────────────────────── -->
      <div class="vd-section" [formGroup]="form">
        <span class="vd-label">{{ 'settings.splitting_rules_when_to_split' | translate }}</span>

        <!-- 2×2 radio card grid -->
        <div class="vd-type-grid">
          <div
            class="vd-type-card"
            *ngFor="let opt of splitTypeOptions"
            [class.vd-type-card--active]="conditionType === opt.value"
            (click)="selectConditionType(opt.value)"
            role="radio"
            [attr.aria-checked]="conditionType === opt.value">
            <span class="vd-type-card__dot">
              <span class="vd-type-card__dot-inner"></span>
            </span>
            <div class="vd-type-card__text">
              <span class="vd-type-card__name">{{ opt.label }}</span>
              <span class="vd-type-card__sub">{{ opt.sub }}</span>
            </div>
          </div>
        </div>

        <!-- Custom field selector -->
        <div *ngIf="conditionType === 'product_custom_field'" class="vd-field-group">
          <label class="vd-sublabel">{{ 'settings.splitting_rules_custom_field_label' | translate }}</label>
          <select class="vd-input vd-select" formControlName="condition_custom_field_id">
            <option [ngValue]="null" disabled>{{ 'settings.splitting_rules_choose_field' | translate }}</option>
            <option *ngFor="let f of productCustomFields" [ngValue]="f.id">
              {{ getCustomFieldLabel(f) }}
            </option>
          </select>
          <span class="vd-hint" *ngIf="productCustomFields.length === 0">
            {{ 'settings.splitting_rules_no_product_fields' | translate }}
          </span>
        </div>

        <!-- Split mode segmented control -->
        <div class="vd-field-group">
          <label class="vd-sublabel">{{ 'settings.splitting_rules_split_mode' | translate }}</label>
          <div class="vd-segment">
            <button type="button" class="vd-segment__btn"
              [class.vd-segment__btn--active]="splitMode === 'all'"
              (click)="setSplitMode('all')">
              {{ 'settings.splitting_rules_all_values' | translate }}
            </button>
            <button type="button" class="vd-segment__btn"
              [class.vd-segment__btn--active]="splitMode === 'specific'"
              [disabled]="!canUseSpecificMode"
              (click)="setSplitMode('specific')">
              {{ 'settings.splitting_rules_specific_values' | translate }}
            </button>
          </div>
          <span class="vd-hint" *ngIf="!canUseSpecificMode">
            {{ 'settings.splitting_rules_label_split_unavailable' | translate }}
          </span>
        </div>

        <!-- Specific values -->
        <div class="vd-field-group" *ngIf="splitMode === 'specific'">
          <label class="vd-sublabel">
            {{ 'settings.splitting_rules_values_to_split' | translate }}
            <span class="vd-sublabel-count" *ngIf="splitValues.length > 0">({{ splitValues.length }})</span>
          </label>

          <!-- Selected chips -->
          <div class="vd-chips" *ngIf="splitValues.length > 0">
            <span class="vd-chip" *ngFor="let v of splitValues">
              {{ v }}
              <button type="button" class="vd-chip__remove" (click)="removeSplitValue(v)" [attr.aria-label]="'settings.splitting_rules_remove' | translate">
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </span>
          </div>

          <!-- Typeahead (brand / category / label / custom_field with field selected) -->
          <div class="vd-typeahead" *ngIf="isTypeaheadSplit">
            <div class="vd-typeahead__wrap">
              <app-svg-icon class="vd-typeahead__icon" svgIcon="search_icon"></app-svg-icon>
              <input
                class="vd-typeahead__input"
                type="text"
                [placeholder]="splitTypeaheadPlaceholder"
                [(ngModel)]="splitSearch"
                [ngModelOptions]="{standalone: true}"
                (focus)="onSplitSearchFocus()"
                (blur)="onSplitSearchBlur()"
                autocomplete="off" />
            </div>
            <!-- Loading indicator for custom field values -->
            <div class="vd-dropdown vd-dropdown--empty" *ngIf="splitDropdownOpen && conditionType === 'product_custom_field' && customFieldValuesLoading">
              <div class="vd-dropdown__empty">{{ 'settings.splitting_rules_loading_values' | translate }}</div>
            </div>
            <div class="vd-dropdown" *ngIf="splitDropdownOpen && !customFieldValuesLoading && filteredSplitOptions.length > 0">
              <div
                class="vd-dropdown__item"
                *ngFor="let opt of filteredSplitOptions"
                (mousedown)="addSplitItem(opt.name)">
                {{ opt.name }}
              </div>
            </div>
            <div class="vd-dropdown vd-dropdown--empty"
              *ngIf="splitDropdownOpen && !customFieldValuesLoading && filteredSplitOptions.length === 0 && (splitSearch.length > 0 || conditionType === 'product_custom_field')">
              <div class="vd-dropdown__empty">
                <ng-container *ngIf="conditionType === 'product_custom_field' && customFieldValues.length === 0 && splitSearch.length === 0">{{ 'settings.splitting_rules_no_values_for_field' | translate }}</ng-container>
                <ng-container *ngIf="splitSearch.length > 0">{{ 'settings.splitting_rules_no_results_for' | translate : { term: splitSearch } }}</ng-container>
              </div>
            </div>
          </div>

          <!-- Free-text input (custom_field only — no field selected yet) -->
          <input
            *ngIf="!isTypeaheadSplit && conditionType === 'product_custom_field'"
            class="vd-dashed-input"
            type="text"
            [placeholder]="specificValuesPlaceholder"
            [(ngModel)]="splitValueInput"
            [ngModelOptions]="{standalone: true}"
            (keydown)="addSplitValueOnEnter($event)"
            (blur)="commitSplitValueInput()" />
          <span class="vd-hint" *ngIf="!isTypeaheadSplit && conditionType === 'product_custom_field'">{{ 'settings.splitting_rules_select_field_hint' | translate }}</span>
        </div>

        <!-- Live preview -->
        <div class="vd-preview">
          <svg class="vd-preview__icon" xmlns="http://www.w3.org/2000/svg" width="13" height="13"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="18" r="3"/>
            <circle cx="6" cy="6" r="3"/>
            <circle cx="18" cy="6" r="3"/>
            <path d="M6 9v1a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V9"/>
            <line x1="12" y1="12" x2="12" y2="15"/>
          </svg>
          <span class="vd-preview__text">{{ previewText }}</span>
        </div>
      </div>

      <div class="vd-divider"></div>

      <!-- ── Apply to ──────────────────────────────────────────────────── -->
      <div class="vd-section">
        <span class="vd-label">{{ 'settings.splitting_rules_apply_to' | translate }}</span>

        <div class="vd-segment">
          <button type="button" class="vd-segment__btn"
            [class.vd-segment__btn--active]="applicabilityMode === 'all'"
            (click)="applicabilityMode = 'all'; conditions = []; conditionSearches = []; conditionDropdownOpen = []">
            {{ 'settings.splitting_rules_all_customers_btn' | translate }}
          </button>
          <button type="button" class="vd-segment__btn"
            [class.vd-segment__btn--active]="applicabilityMode === 'conditions'"
            (click)="applicabilityMode = 'conditions'">
            {{ 'settings.splitting_rules_matching_conditions' | translate }}
          </button>
        </div>

        <!-- All customers hint -->
        <div class="vd-all-customers" *ngIf="applicabilityMode === 'all'">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          <span [innerHTML]="'settings.splitting_rules_all_customers_hint' | translate"></span>
        </div>

        <!-- Condition builder -->
        <div class="vd-conditions" *ngIf="applicabilityMode === 'conditions'">

          <div class="vd-condition-card" *ngFor="let c of conditions; let i = index">

            <!-- Type row -->
            <div class="vd-condition-row">
              <select class="vd-input vd-select vd-condition__type-select"
                [value]="c.type"
                (change)="setConditionType(i, $any($event.target).value)">
                <option value="customer">{{ 'settings.splitting_rules_type_customer' | translate }}</option>
                <option value="customer_group">{{ 'settings.splitting_rules_type_customer_group' | translate }}</option>
                <option value="custom_field">{{ 'settings.splitting_rules_type_custom_field' | translate }}</option>
              </select>
              <button type="button" class="vd-condition__remove" (click)="removeCondition(i)" [attr.aria-label]="'settings.splitting_rules_remove_condition' | translate">
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <!-- Customer typeahead -->
            <ng-container *ngIf="c.type === 'customer'">
              <div class="vd-chips vd-chips--condition" *ngIf="c.customerChips.length > 0">
                <span class="vd-chip" *ngFor="let ch of c.customerChips">
                  {{ ch.name }}
                  <button type="button" class="vd-chip__remove" (click)="removeCustomerChip(i, ch.id)" [attr.aria-label]="'settings.splitting_rules_remove' | translate">
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </span>
              </div>
              <div class="vd-typeahead">
                <div class="vd-typeahead__wrap">
                  <app-svg-icon class="vd-typeahead__icon" svgIcon="search_icon"></app-svg-icon>
                  <input
                    class="vd-typeahead__input"
                    type="text"
                    [placeholder]="'settings.splitting_rules_search_customers' | translate"
                    [value]="conditionSearches[i] || ''"
                    (input)="setConditionSearch(i, $any($event.target).value)"
                    (focus)="onConditionSearchFocus(i)"
                    (blur)="onConditionSearchBlur(i)"
                    autocomplete="off" />
                </div>
                <div class="vd-dropdown" *ngIf="conditionDropdownOpen[i] && conditionCustomerLoading[i]">
                  <div class="vd-dropdown__loading">
                    <span class="vd-spinner-sm"></span>
                    {{ 'settings.splitting_rules_searching' | translate }}
                  </div>
                </div>
                <div class="vd-dropdown" *ngIf="conditionDropdownOpen[i] && !conditionCustomerLoading[i] && getFilteredCustomers(i).length > 0">
                  <div
                    class="vd-dropdown__item"
                    *ngFor="let cu of getFilteredCustomers(i)"
                    (mousedown)="addCustomerChip(i, { id: cu.id, name: cu.name })">
                    <span class="vd-dropdown__name">{{ cu.name }}</span>
                    <span class="vd-dropdown__id">#{{ cu.id }}</span>
                  </div>
                </div>
                <div class="vd-dropdown vd-dropdown--empty"
                  *ngIf="conditionDropdownOpen[i] && !conditionCustomerLoading[i] && getFilteredCustomers(i).length === 0">
                  <div class="vd-dropdown__empty">{{ 'settings.splitting_rules_no_customers_found' | translate }}</div>
                </div>
              </div>
            </ng-container>

            <!-- Customer group typeahead -->
            <ng-container *ngIf="c.type === 'customer_group'">
              <div class="vd-chips vd-chips--condition" *ngIf="c.customerChips.length > 0">
                <span class="vd-chip" *ngFor="let ch of c.customerChips">
                  {{ ch.name }}
                  <button type="button" class="vd-chip__remove" (click)="removeCustomerChip(i, ch.id)" [attr.aria-label]="'settings.splitting_rules_remove' | translate">
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </span>
              </div>
              <div class="vd-typeahead">
                <div class="vd-typeahead__wrap">
                  <app-svg-icon class="vd-typeahead__icon" svgIcon="search_icon"></app-svg-icon>
                  <input
                    class="vd-typeahead__input"
                    type="text"
                    [placeholder]="'settings.splitting_rules_search_groups' | translate"
                    [value]="conditionSearches[i] || ''"
                    (input)="setConditionSearch(i, $any($event.target).value)"
                    (focus)="onConditionSearchFocus(i)"
                    (blur)="onConditionSearchBlur(i)"
                    autocomplete="off" />
                </div>
                <div class="vd-dropdown" *ngIf="conditionDropdownOpen[i] && getFilteredCustomerGroups(i).length > 0">
                  <div
                    class="vd-dropdown__item"
                    *ngFor="let g of getFilteredCustomerGroups(i)"
                    (mousedown)="addCustomerGroupChip(i, g)">
                    <span class="vd-dropdown__name">{{ g.name }}</span>
                  </div>
                </div>
                <div class="vd-dropdown vd-dropdown--empty"
                  *ngIf="conditionDropdownOpen[i] && (conditionSearches[i] || '').length > 0 && getFilteredCustomerGroups(i).length === 0">
                  <div class="vd-dropdown__empty">{{ 'settings.splitting_rules_no_groups_found' | translate }}</div>
                </div>
              </div>
            </ng-container>

            <!-- Custom field condition -->
            <ng-container *ngIf="c.type === 'custom_field'">
              <select class="vd-input vd-select"
                (change)="setConditionFieldId(i, +$any($event.target).value)">
                <option value="" [selected]="!c.fieldId" disabled>{{ 'settings.splitting_rules_choose_customer_field' | translate }}</option>
                <option *ngFor="let f of customerCustomFields" [value]="f.id" [selected]="c.fieldId === f.id">
                  {{ f.label }}
                </option>
              </select>
              <span class="vd-hint" *ngIf="customerCustomFields.length === 0 && !customerCustomFieldsLoading">
                {{ 'settings.splitting_rules_no_customer_fields' | translate }}
              </span>
              <!-- Value typeahead -->
              <div class="vd-typeahead" *ngIf="c.fieldId">
                <div class="vd-typeahead__wrap">
                  <app-svg-icon class="vd-typeahead__icon" svgIcon="search_icon"></app-svg-icon>
                  <input
                    class="vd-typeahead__input"
                    type="text"
                    [placeholder]="'settings.splitting_rules_search_or_type' | translate"
                    [value]="conditionValueSearches[i] ?? c.fieldValue"
                    (input)="setConditionValueSearch(i, $any($event.target).value)"
                    (focus)="onConditionValueFocus(i)"
                    (blur)="onConditionValueBlur(i)"
                    autocomplete="off" />
                </div>
                <div class="vd-dropdown"
                  *ngIf="conditionValueDropdownOpen[i] && getFilteredConditionValueOptions(i).length > 0">
                  <div class="vd-dropdown__item"
                    *ngFor="let v of getFilteredConditionValueOptions(i)"
                    (mousedown)="selectConditionValueFromDropdown(i, v)">
                    {{ v }}
                  </div>
                </div>
                <div class="vd-dropdown vd-dropdown--empty"
                  *ngIf="conditionValueDropdownOpen[i] && getFilteredConditionValueOptions(i).length === 0 && (conditionValueSearches[i] || '').length > 0">
                  <div class="vd-dropdown__empty">{{ 'settings.splitting_rules_no_results_saved' | translate }}</div>
                </div>
              </div>
              <span class="vd-hint" *ngIf="!c.fieldId">{{ 'settings.splitting_rules_select_field_values' | translate }}</span>
            </ng-container>

          </div>

          <!-- Add condition -->
          <button type="button" class="vd-add-condition-btn" (click)="addCondition()">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            {{ 'settings.splitting_rules_add_condition' | translate }}
          </button>

        </div>
      </div>

    </div><!-- /body -->

    <!-- ── Footer ─────────────────────────────────────────────────────── -->
    <div class="vd-footer">
      <ui-button
        type="secondary"
        [label]="'settings.splitting_rules_cancel' | translate | uppercase"
        (click)="closeDrawer.emit()">
      </ui-button>
      <ui-button
        type="primary"
        [label]="(rule ? ('settings.splitting_rules_save_changes' | translate) : ('settings.splitting_rules_save_rule' | translate)) | uppercase"
        icon="check_icon"
        iconPosition="start"
        [disabled]="!isValid"
        (click)="submit()">
      </ui-button>
    </div>

  </div>

</ng-container>
